import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const jobs = [
  {
    id: 1,
    title: 'Software Development Engineer',
    company: 'Fractal Analytics',
    location: 'Bangalore, India',
    start: '2025-02-01',
    end: null,
    color: '#818cf8',
    highlights: [
      'Architected a pluggable Agent Hub orchestrator in NestJS with standardized Agent Communication Protocol (ACP)',
      'Engineered event-driven data pipeline using Redis Streams for real-time UI streaming',
      'Built scalable backend services with LangChain, FastAPI, and Azure Vector Search',
      'Developed interactive frontend for conversational AI using Next.js and Tailwind CSS',
    ],
  },
  {
    id: 2,
    title: 'Software Development Engineer',
    company: 'Daffodil Software Pvt Ltd',
    location: '',
    start: '2022-01-01',
    end: '2025-02-01',
    color: '#22d3ee',
    highlights: [
      'Developed loan management system for fintech using NestJS, MySQL, and event-driven architecture',
      'Integrated BullMQ for microservice communication',
      'Integrated third-party adapters reducing loan processing time by 70%',
      'Implemented event-driven audit log for regulatory compliance',
      'Built scalable NodeJS-based Enterprise Service Bus managing 29 concurrent services',
      'Implemented Hasura triggers with FCM, improving user engagement by 70%',
      'Designed image/video processing pipeline using Cloud Pub/Sub',
    ],
  },
];

export default function TimelineViz() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [activeJob, setActiveJob] = useState(0);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = containerRef.current.getBoundingClientRect().width;
    const height = 140;
    const padding = 50;

    svg.attr('width', width).attr('height', height);

    const parseDate = d3.timeParse('%Y-%m-%d');
    const startDate = parseDate('2022-01-01');
    const endDate = new Date();

    const xScale = d3.scaleTime().domain([startDate, endDate]).range([padding, width - padding]);

    // Track line
    svg.append('line')
      .attr('x1', padding).attr('y1', height / 2)
      .attr('x2', width - padding).attr('y2', height / 2)
      .attr('stroke', '#1e2740').attr('stroke-width', 5)
      .attr('stroke-linecap', 'round');

    // Job bars
    jobs.forEach((job, i) => {
      const x1 = xScale(parseDate(job.start));
      const x2 = job.end ? xScale(parseDate(job.end)) : xScale(endDate);

      // Active bar glow
      if (i === activeJob) {
        svg.append('line')
          .attr('x1', x1).attr('y1', height / 2)
          .attr('x2', x2).attr('y2', height / 2)
          .attr('stroke', job.color).attr('stroke-width', 18)
          .attr('stroke-linecap', 'round').attr('stroke-opacity', 0.12);
      }

      svg.append('line')
        .attr('x1', x1).attr('y1', height / 2)
        .attr('x2', x2).attr('y2', height / 2)
        .attr('stroke', job.color)
        .attr('stroke-width', i === activeJob ? 8 : 5)
        .attr('stroke-linecap', 'round')
        .attr('stroke-opacity', i === activeJob ? 1 : 0.4)
        .style('cursor', 'pointer')
        .on('click', () => setActiveJob(i));

      // Start dot
      svg.append('circle')
        .attr('cx', x1).attr('cy', height / 2).attr('r', i === activeJob ? 12 : 8)
        .attr('fill', job.color).attr('stroke', '#0a0f1a').attr('stroke-width', 3)
        .style('cursor', 'pointer')
        .on('click', () => setActiveJob(i));

      // End dot
      svg.append('circle')
        .attr('cx', x2).attr('cy', height / 2).attr('r', i === activeJob ? 12 : 8)
        .attr('fill', job.end ? job.color : 'none')
        .attr('stroke', job.color).attr('stroke-width', 3)
        .attr('stroke-dasharray', job.end ? 'none' : '4,3')
        .style('cursor', 'pointer')
        .on('click', () => setActiveJob(i));

      // Year labels
      const startYear = parseDate(job.start).getFullYear();
      svg.append('text')
        .attr('x', x1).attr('y', height / 2 + 30)
        .attr('text-anchor', 'middle')
        .attr('fill', '#64748b').attr('font-size', '13px')
        .text(startYear);
    });

    // "Now" label
    svg.append('text')
      .attr('x', xScale(endDate)).attr('y', height / 2 + 30)
      .attr('text-anchor', 'middle')
      .attr('fill', '#818cf8').attr('font-size', '13px').attr('font-weight', '600')
      .text('Now');

    // Pulse on active end dot if current
    if (!jobs[activeJob].end) {
      const pulseX = xScale(endDate);
      svg.append('circle')
        .attr('cx', pulseX).attr('cy', height / 2).attr('r', 12)
        .attr('fill', 'none').attr('stroke', jobs[activeJob].color).attr('stroke-width', 2)
        .attr('opacity', 1)
        .transition().duration(1500).ease(d3.easeCubicOut)
        .attr('r', 28).attr('opacity', 0)
        .on('end', function repeat() {
          d3.select(this).attr('r', 12).attr('opacity', 1)
            .transition().duration(1500).ease(d3.easeCubicOut)
            .attr('r', 28).attr('opacity', 0)
            .on('end', repeat);
        });
    }
  }, [activeJob]);

  const job = jobs[activeJob];

  return (
    <div id="employment" className="mt-16 py-12 text-white">
      <h1 className="text-3xl md:text-4xl font-bold text-center">Employment</h1>
      <p className="font-light text-slate-400 mt-2 text-center">Click on the timeline to explore</p>

      <div ref={containerRef} className="mt-8 max-w-3xl mx-auto">
        <svg ref={svgRef} className="w-full" style={{ touchAction: 'none' }} />
      </div>

      <div className="max-w-3xl mx-auto mt-6" data-aos="fade-up" data-aos-duration="400" key={job.id}>
        <div className="bg-dark-200 rounded-2xl border p-6 md:p-8 transition-all duration-500" style={{ borderColor: job.color + '30' }}>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-xl md:text-2xl font-bold">{job.title}</h2>
              <p className="text-sm font-medium mt-1" style={{ color: job.color }}>
                {job.company}{job.location ? `, ${job.location}` : ''} &middot; {new Date(job.start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} &ndash; {job.end ? new Date(job.end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
              </p>
            </div>
            {!job.end && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: job.color + '20', color: job.color }}>
                Current
              </span>
            )}
          </div>

          <ul className="mt-5 space-y-3">
            {job.highlights.map((h, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: job.color }}></span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Job selector pills for mobile */}
      <div className="flex justify-center gap-2 mt-4 md:hidden">
        {jobs.map((j, i) => (
          <button
            key={j.id}
            onClick={() => setActiveJob(i)}
            className={`px-3 py-1 rounded-full text-xs transition-all ${i === activeJob ? 'font-semibold' : 'text-slate-500'}`}
            style={i === activeJob ? { backgroundColor: j.color + '20', color: j.color } : {}}
          >
            {j.company.split(' ')[0]}
          </button>
        ))}
      </div>
    </div>
  );
}
